import shopApi from "../../api/shop";

const SEARCH_FILLER_WORDS = new Set([
  "a", "an", "and", "business", "company", "for", "i", "in", "license",
  "licence", "looking", "need", "of", "package", "packages", "setup", "the",
  "to", "visa", "visas", "want", "with",
]);

// Keep corrections scoped to package vocabulary; never guess short codes or IDs.
const SEARCH_VOCABULARY = [...new Set([
  ...SEARCH_FILLER_WORDS,
  "dubai", "ajman", "sharjah", "abu", "dhabi", "ras", "khaimah", "umm", "quwain",
  "fujairah", "free", "zone", "freezone", "mainland", "offshore", "freelance",
  "freelancer", "trading", "general", "commercial", "professional", "industrial",
  "consultancy", "consulting", "services", "media", "city", "technology",
  "ecommerce", "commerce", "tourism", "travel", "real", "estate", "investment",
  "management", "marketing", "education", "healthcare", "restaurant", "food",
  "transport", "logistics", "construction", "cleaning", "technical", "accounting",
])];

const spellingDistance = (left, right) => {
  const rows = Array.from({ length: left.length + 1 }, (_, index) => [index]);
  for (let j = 0; j <= right.length; j += 1) rows[0][j] = j;
  for (let i = 1; i <= left.length; i += 1) {
    for (let j = 1; j <= right.length; j += 1) {
      rows[i][j] = Math.min(
        rows[i - 1][j] + 1,
        rows[i][j - 1] + 1,
        rows[i - 1][j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1),
      );
      if (i > 1 && j > 1 && left[i - 1] === right[j - 2] && left[i - 2] === right[j - 1]) {
        // Prefer swapped letters over an equally close substitution (duabi -> dubai).
        rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 0.75);
      }
    }
  }
  return rows[left.length][right.length];
};

const correctSearchSpelling = (value) => value.replace(/[a-z0-9]+/gi, (token) => {
  const word = token.toLowerCase();
  if (!/^[a-z]{4,24}$/.test(word) || SEARCH_VOCABULARY.includes(word)) return token;
  const maxDistance = word.length >= 7 ? 2 : 1;
  let closest = [];
  let bestDistance = maxDistance + 1;
  SEARCH_VOCABULARY.forEach((candidate) => {
    if (Math.abs(candidate.length - word.length) > maxDistance) return;
    const distance = spellingDistance(word, candidate);
    if (distance < bestDistance) {
      bestDistance = distance;
      closest = [candidate];
    } else if (distance === bestDistance) closest.push(candidate);
  });
  return bestDistance <= maxDistance && closest.length === 1 ? closest[0] : token;
});

const getMeaningfulWords = (value) => value
  .toLowerCase()
  .replace(/[^a-z0-9\s-]/g, " ")
  .split(/\s+/)
  .filter((word) => word && !SEARCH_FILLER_WORDS.has(word) && !/^\d+$/.test(word));

// Both search endpoints accept regular expressions. Generate only bounded,
// escaped alternatives so activity names do not depend on a fixed dictionary.
const buildTypoPattern = (words) => {
  if (!words.length || words.length > 8 || !words.some((word) => /^[a-z]{4,24}$/.test(word))) return "";
  const patterns = words.map((word) => {
    if (!/^[a-z]{4,24}$/.test(word)) return word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const variants = new Set([word]);
    for (let i = 0; i < word.length; i += 1) {
      variants.add(word.slice(0, i) + word.slice(i + 1));
      variants.add(`${word.slice(0, i)}[a-z]${word.slice(i + 1)}`);
      variants.add(`${word.slice(0, i)}[a-z]${word.slice(i)}`);
      if (i + 1 < word.length) {
        variants.add(word.slice(0, i) + word[i + 1] + word[i] + word.slice(i + 2));
      }
    }
    variants.add(`${word}[a-z]`);
    return `\\b(?:${[...variants].join("|")})\\b`;
  });
  return `^${patterns.map((pattern) => `(?=.*${pattern})`).join("")}.*`;
};

export const buildSearchCandidates = (value) => {
  const original = value.trim().replace(/\s+/g, " ");
  const corrected = correctSearchSpelling(original);
  const words = getMeaningfulWords(original);
  const correctedWords = getMeaningfulWords(corrected);
  const phraseCandidates = [];

  for (let start = 0; start < words.length - 1; start += 1) {
    phraseCandidates.push(words.slice(start, start + 2).join(" "));
  }

  return [...new Set([
    original,
    ...(corrected !== original ? [corrected, correctedWords.join(" ")] : []),
    words.join(" "),
    ...phraseCandidates,
    ...words.sort((a, b) => b.length - a.length),
    ...(corrected !== original ? correctedWords.sort((a, b) => b.length - a.length) : []),
    buildTypoPattern(getMeaningfulWords(original)),
  ].filter((item) => item.length >= 2))];
};

export const rankProducts = (items, searchValue) => {
  const normalizedQuery = searchValue.toLowerCase().replace(/[^a-z0-9\s-]/g, " ").replace(/\s+/g, " ").trim();
  const queryWords = getMeaningfulWords(normalizedQuery);

  const scoreProduct = (product) => {
    const title = (product.title || "").toLowerCase();
    const searchable = [product.title, product.sku, product.shortDescription, product.description]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    const matchedWords = queryWords.filter((word) => searchable.includes(word));
    let score = matchedWords.length * 100;

    if (normalizedQuery && searchable.includes(normalizedQuery)) score += 1000;
    if (queryWords.length && matchedWords.length === queryWords.length) score += 500;
    queryWords.forEach((word, index) => {
      if (title.includes(word)) score += 35;
      if (title.startsWith(word)) score += 15;
      if (index > 0 && title.includes(`${queryWords[index - 1]} ${word}`)) score += 50;
    });

    return score;
  };

  return items
    .map((product, index) => ({ product, index, score: scoreProduct(product) }))
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map(({ product }) => product);
};

export const getSmartProductSuggestions = async ({ query, category, limit = 6 }) => {
  const candidates = buildSearchCandidates(query);

  for (const candidate of candidates) {
    const options = { search: candidate };
    if (category && category !== "all") options.category = category;
    const result = await shopApi.getSuggestions(options);
    const matches = Array.isArray(result) ? result : [];

    if (matches.length > 0) {
      return {
        products: rankProducts(matches, query).slice(0, limit),
        effectiveQuery: candidate.startsWith("^(?=") ? query.trim() : candidate,
      };
    }
  }

  return {
    products: [],
    effectiveQuery: candidates[candidates.length - 1] || query.trim(),
  };
};
